/** Campos de cabecera cuya procedencia en el documento se puede resaltar. */
export type SourceField = "date" | "supplierTaxId" | "supplier" | "invoiceNumber" | "totalAmount";

export const SOURCE_FIELDS: SourceField[] = ["date", "supplierTaxId", "supplier", "invoiceNumber", "totalAmount"];

export const SOURCE_FIELD_LABELS: Record<SourceField, string> = {
  date: "Fecha",
  supplierTaxId: "NIF / CIF",
  supplier: "Proveedor",
  invoiceNumber: "Nº Factura",
  totalAmount: "Total",
};

/** Caja normalizada 0-1 (top, left, bottom, right) sobre una página (1-based). */
export interface FieldLocation {
  page: number;
  top: number;
  left: number;
  bottom: number;
  right: number;
}

export type FieldLocations = Partial<Record<SourceField, FieldLocation>>;

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

/**
 * Convierte la respuesta de la IA ({page, box:[ymin,xmin,ymax,xmax]} en escala
 * 0-1000) en cajas normalizadas 0-1. Descarta entradas inválidas.
 */
export function sanitizeFieldLocations(raw: unknown): FieldLocations | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const out: FieldLocations = {};
  for (const key of SOURCE_FIELDS) {
    const v = (raw as Record<string, any>)[key];
    if (!v || typeof v !== "object") continue;
    const box = Array.isArray(v.box) ? v.box.map(Number) : null;
    if (!box || box.length !== 4 || box.some((n: number) => !Number.isFinite(n))) continue;
    let [ymin, xmin, ymax, xmax] = box;
    if (ymin > ymax) [ymin, ymax] = [ymax, ymin];
    if (xmin > xmax) [xmin, xmax] = [xmax, xmin];
    if (ymax - ymin <= 0 || xmax - xmin <= 0) continue;
    const page = Math.max(1, Math.round(Number(v.page) || 1));
    out[key] = {
      page,
      top: clamp01(ymin / 1000),
      left: clamp01(xmin / 1000),
      bottom: clamp01(ymax / 1000),
      right: clamp01(xmax / 1000),
    };
  }
  return Object.keys(out).length ? out : undefined;
}

/** Fragmento de texto de una página con su caja normalizada 0-1. */
export interface PageTextItem {
  str: string;
  top: number;
  left: number;
  bottom: number;
  right: number;
}

/** Une, en orden de lectura, el texto cuyo centro cae dentro del rectángulo. */
export function textInRect(items: PageTextItem[], rect: { top: number; left: number; bottom: number; right: number }): string {
  const inside = items.filter((it) => {
    const cx = (it.left + it.right) / 2;
    const cy = (it.top + it.bottom) / 2;
    return it.str.trim() && cx >= rect.left && cx <= rect.right && cy >= rect.top && cy <= rect.bottom;
  });
  if (!inside.length) return "";
  inside.sort((a, b) => {
    const h = Math.max(0.004, Math.min(a.bottom - a.top, b.bottom - b.top) / 2);
    if (Math.abs(a.top - b.top) > h) return a.top - b.top;
    return a.left - b.left;
  });
  return inside.map((i) => i.str.trim()).join(" ").replace(/\s+/g, " ").trim();
}

/** Valor extraído que se quiere localizar en el texto del PDF. */
export interface ExtractedValue {
  key: string;
  label: string;
  value: string | number | undefined | null;
  kind: "text" | "taxId" | "date" | "amount";
}

const norm = (s: string) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().replace(/\s+/g, "");

function amountVariants(n: number): string[] {
  const abs = Math.abs(n);
  const fixed = abs.toFixed(2);
  const [int, dec] = fixed.split(".");
  const dots = int.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const commas = int.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return Array.from(new Set([`${dots},${dec}`, `${int},${dec}`, `${commas}.${dec}`, `${int}.${dec}`]));
}

function dateVariants(iso: string): string[] {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return [iso];
  const [, y, mo, d] = m;
  const out: string[] = [];
  for (const sep of ["/", "-", "."]) {
    out.push(`${d}${sep}${mo}${sep}${y}`, `${d}${sep}${mo}${sep}${y.slice(2)}`);
  }
  return out;
}

/** Normaliza manteniendo la posición de cada carácter en el texto original. */
function indexedText(text: string, taxId: boolean) {
  let normalized = "";
  const positions: number[] = [];
  Array.from(text).forEach((char, index) => {
    const part = norm(char);
    for (const c of part) {
      if (taxId && !/[A-Z0-9]/.test(c)) continue;
      normalized += c;
      positions.push(index);
    }
  });
  return { normalized, positions };
}

const legalLegend = /protecci[oó]n\s+de\s+datos|datos\s+personales|pol[ií]tica\s+de\s+privacidad|reglamento\s+general|responsable\s+del\s+tratamiento|RGPD|LOPD|derechos\s+de\s+acceso|registro\s+mercantil/i;

/** Localiza una aparición completa por dato y recorta la caja al valor, no a su leyenda. */
export function locateExtractedValues(
  pages: PageTextItem[][],
  values: ExtractedValue[],
): { page: number; key: string; label: string; top: number; left: number; bottom: number; right: number }[] {
  const out: { page: number; key: string; label: string; top: number; left: number; bottom: number; right: number }[] = [];
  for (const v of values) {
    if (v.value === undefined || v.value === null || v.value === "") continue;
    let needles: string[] = [];
    if (v.kind === "amount") {
      const n = Number(v.value);
      if (!Number.isFinite(n) || n === 0) continue;
      needles = amountVariants(n);
    } else if (v.kind === "date") needles = dateVariants(String(v.value));
    else if (v.kind === "taxId") needles = [norm(String(v.value)).replace(/[^A-Z0-9]/g, "").replace(/^ES/, "")];
    else needles = [norm(String(v.value))];
    needles = needles.map(norm).filter((n) => n.length >= 3);
    if (!needles.length) continue;
    const candidates: (typeof out[number] & { score: number })[] = [];
    pages.forEach((items, idx) => {
      for (const it of items) {
        if (legalLegend.test(it.str)) continue;
        const { normalized: s, positions } = indexedText(it.str, v.kind === "taxId");
        if (!s) continue;
        for (const nd of needles) {
          const i = s.indexOf(nd);
          if (i < 0) continue;
          const before = s[i - 1];
          const after = s[i + nd.length];
          if (v.kind === "amount") {
            if ((before && /[0-9.,]/.test(before)) || (after && /[0-9]/.test(after))) continue;
          } else {
            // No confundir F-12 con F-123 ni un NIF con parte de otro identificador.
            const start = positions[i];
            const end = positions[i + nd.length - 1];
            if ((start > 0 && /[A-Z0-9]/i.test(it.str[start - 1])) || /[A-Z0-9]/i.test(it.str[end + 1] || "")) continue;
          }
          const start = positions[i];
          const end = positions[i + nd.length - 1] + 1;
          const width = it.right - it.left;
          const hasLabel = v.key === "totalAmount" && /\btotal\b/i.test(it.str)
            || v.key === "invoiceNumber" && /factura|fra\b/i.test(it.str)
            || v.kind === "taxId" && /\b(NIF|CIF)\b/i.test(it.str);
          candidates.push({
            page: idx + 1, key: v.key, label: v.label,
            top: it.top, bottom: it.bottom,
            left: it.left + width * start / it.str.length,
            right: it.left + width * end / it.str.length,
            score: (hasLabel ? 2 : 0) + nd.length / s.length,
          });
          break;
        }
      }
    });
    candidates.sort((a, b) => b.score - a.score);
    const best = candidates[0];
    if (best) {
      const { score: _score, ...match } = best;
      out.push(match);
    }
  }
  return out;
}
